import { consoleConfig } from '../config';

/*
 * A first sign-in with no organization gets the same personal org the desktop
 * sign-in creates (collabv3 `src/index.ts`, the discovery path): the same
 * name and session length, then the server-managed `personal` tag, which only
 * the worker can write. Desktop recognises the org as personal by that tag.
 */

/** Desktop's session length for the org it creates at sign-in: one week. */
const PERSONAL_ORG_SESSION_MINUTES = 60 * 24 * 7;

/** Desktop's name for it: "<email local part>'s Workspace". */
export function personalOrgName(email: string): string {
  return `${email.split('@')[0]}'s Workspace`;
}

interface DiscoveryCreateClient {
  discovery: {
    organizations: {
      create(options: { organization_name: string; session_duration_minutes: number }): Promise<unknown>;
    };
  };
}

/** Creates the org from the discovery session and returns the new org-scoped session JWT. */
export async function createPersonalOrg(stytch: DiscoveryCreateClient, email: string): Promise<string | null> {
  const result = await stytch.discovery.organizations.create({
    organization_name: personalOrgName(email),
    session_duration_minutes: PERSONAL_ORG_SESSION_MINUTES,
  }) as { session_jwt?: string | null };
  return typeof result.session_jwt === 'string' && result.session_jwt ? result.session_jwt : null;
}

export type PersonalTagOutcome = 'tagged' | 'not_eligible' | 'failed';

/**
 * Asks the worker to tag the JWT's org as personal. Idempotent. Never throws:
 * the org works as personal without the tag, so a failure is logged and the
 * next sign-in to it tries again (see `shouldRetryPersonalTag`).
 */
export async function tagPersonalOrg(orgSessionJwt: string): Promise<PersonalTagOutcome> {
  try {
    const response = await fetch(`${consoleConfig.collabApiBaseUrl}/api/personal-org/tag`, {
      method: 'POST',
      credentials: 'omit',
      headers: { Accept: 'application/json', Authorization: `Bearer ${orgSessionJwt}` },
    });
    if (response.ok) return 'tagged';
    const body = await response.json().catch(() => null) as { error?: string } | null;
    console.warn('[auth] personal org tag refused', response.status, body?.error);
    return response.status === 403 && body?.error === 'not_eligible' ? 'not_eligible' : 'failed';
  } catch (cause) {
    console.warn('[auth] personal org tag failed', cause);
    return 'failed';
  }
}

/**
 * A later sign-in retries only for an org that looks like one this flow made
 * and is still untagged, so ordinary sign-ins make no extra call.
 */
export function shouldRetryPersonalTag(
  organization: { organization_name?: string; trusted_metadata?: Record<string, unknown> | null },
  email: string | null | undefined,
): boolean {
  if (!email || organization.organization_name !== personalOrgName(email)) return false;
  return !organization.trusted_metadata?.nimbalyst_org_type;
}
